import React, { lazy, Suspense } from "react";
import _ from "lodash";
import { SnapshotAPI } from "apis";
import { orderNetworksBy, searchNetworkByInput } from "../../helpers/Snapshot";
import {
  snapshotSortKeyMap,
  snapshotSortByDayKeyMap,
  SNAPSHOT_PRESETS,
  SNAPSHOT_PRESET_CONFIGS,
} from "./../../constants/SortTypes";
import { trialTypes, subFilterTypes } from "../../constants/FilterTypes";
import { networkProduct } from "../../constants/NetworkProduct";
import { networkStatus } from "../../constants/NetworkStatus";
import LoadingUI from "../common/LoadingUI";
import FixedWidgetContainer from "components/common/FixedWidgetContainer";
import NetworkStickyTimeWidget from "../common/NetworkStickyTimeWidget";
import ToolBoxWidget from "../common/ToolBoxWidget";
import QuickNavigateHeader from "./QuickNavigateHeader";
import { PREFERENCES } from "constants/Preferences";
import { notify } from "react-notify-toast";
import { getItemBase64 } from "helpers/LocalStorage";
const SnapshotViewHeader = lazy(() => import("./SnapshotViewHeader"));
const NetworkWrap = lazy(() => import("./NetworkWrap"));

const NUM_PER_PAGE = 10;

class SnapshotViewer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      keyParams: _.get(this.props, "match.params.snapshotKey"),

      isLoading: false,
      snapshotKey: null,
      data: null,
      reportCreateTime: null,
      dataGroupedByNetworkStatus: null,

      filteredData: null,
      paginatedData: [],

      // Show data in TWD?
      isInTwd: true,
      // Expand all trend table
      showTrendTable: false,
      selectedSortType: snapshotSortKeyMap.BILLABLE_INCREASED_REVENUE,
      selectedSortByDayType: snapshotSortByDayKeyMap.PAST_7_DAYS_AVG,
      isOrderByDesc: true,

      selectedFilterType: networkStatus.RUNNING,
      // selectedTrialType: trialTypes.ALL,
      selectedSubFilter: subFilterTypes.ALL,
      filterTabs: null,
      subFilterTabs: null,
      trialFilterTabs: null,
      productFilterTabs: null,

      // if user is using search bar
      isInSearchingMode: false,
      numberOfSearchedData: 0,

      // for navigator panel
      selectedTabKey: "custom",
      inputValue: "",
      customData: null,

      // for unit tabs
      customTabs: null,

      // for indicators
      selectedIndKey: "custom",
      customIndicators: null,

      // for custom units
      customUnits: null,

      // for custom network groups
      customNetworkGroups: [],
      networkInfo: [],

      errMsg: null,
    };

    this.handleFilterNetworks = this.handleFilterNetworks.bind(this);
    this.handleSearch = this.handleSearch.bind(this);

    this.toggleSetTwd = this.toggleSetTwd.bind(this);
    this.handleSortTypeChange = this.handleSortTypeChange.bind(this);
    this.handleSortByDayTypeChange = this.handleSortByDayTypeChange.bind(this);
    this.toggleOrderBy = this.toggleOrderBy.bind(this);
    this.handlePresetProcess = this.handlePresetProcess.bind(this);

    this.handleFilterTypeChange = this.handleFilterTypeChange.bind(this);
    this.handleSubFilterChange = this.handleSubFilterChange.bind(this);
    this.handleCustomNetworkGroupingChanged =
      this.handleCustomNetworkGroupingChanged.bind(this);

    this.handleLoadMore = this.handleLoadMore.bind(this);

    this.handleCreateNetworkSnapshot =
      this.handleCreateNetworkSnapshot.bind(this);
    this.handleCreateYieldSetSnapshot =
      this.handleCreateYieldSetSnapshot.bind(this);
    this.handleForceRefreshSnapshot =
      this.handleForceRefreshSnapshot.bind(this);

    this.getDataByFilterType = this.getDataByFilterType.bind(this);
    this._parsedSubFilter = this._parsedSubFilter.bind(this);

    this.itemRefs = [];
    this.widgetRef = React.createRef();
  }

  async componentDidMount() {
    // document.title = "Lastest Snapshot | YB Observer";

    this.setState({ isLoading: true });
    try {
      const snapshot = await SnapshotAPI.getSnapshot(this.state.keyParams);
      if (snapshot) {
        // for header tabs
        const dataGroupedByNetworkStatus = _.groupBy(snapshot.data, "status");
        let filterTabs = _.map(networkStatus, (status) => {
          const numOfNetworksByFilter = dataGroupedByNetworkStatus[status]
            ? dataGroupedByNetworkStatus[status].length
            : 0;

          switch (status) {
            case networkStatus.RUNNING: {
              return {
                title: `Running (${numOfNetworksByFilter})`,
                filterValue: status,
              };
            }
            case networkStatus.PAUSED: {
              return {
                title: `Paused (${numOfNetworksByFilter})`,
                filterValue: status,
              };
            }
            case networkStatus.PROBING: {
              return {
                title: `Probing (${numOfNetworksByFilter})`,
                filterValue: status,
              };
            }
            case networkStatus.TERMINATED: {
              return {
                title: `Terminated (${numOfNetworksByFilter})`,
                filterValue: status,
              };
            }

            default: {
              // nothing
            }
          }
        });
        filterTabs = _.compact(filterTabs);

        // Preferences: get custom data for navigator
        const customState = {};
        const savedData = getItemBase64(PREFERENCES.NAVIGATOR);
        if (savedData) {
          customState.customData = savedData;
        }

        // Preferences: get custom data for unit tabs
        const savedTabs = getItemBase64(PREFERENCES.TABS);
        if (savedTabs) {
          customState.customTabs = savedTabs;
        }

        // Preferences: get custom data for indicators
        const savedIndicators = getItemBase64(PREFERENCES.INDICATORS);
        if (savedIndicators) {
          customState.customIndicators = savedIndicators;
        }

        // Preferences: get custom data for units
        const savedUnits = getItemBase64(PREFERENCES.UNITS);
        if (savedUnits) {
          customState.customUnits = savedUnits;
        }

        const customNetworkGroupsTab = {
          title: "Favorites (0)",
          filterValue: -1,
        };

        let selectedFilterType = networkStatus.RUNNING;
        let groupedNetwork = dataGroupedByNetworkStatus[selectedFilterType];

        // Preferences: get custom data for network groups
        const savedNetworkGroups = getItemBase64(PREFERENCES.NETWORK_GROUPS);
        if (savedNetworkGroups) {
          customState.customNetworkGroups = savedNetworkGroups;
          customNetworkGroupsTab.title = `Favorites (${savedNetworkGroups.length})`;
          if (savedNetworkGroups.length > 0) {
            groupedNetwork = snapshot.data.filter((d) =>
              savedNetworkGroups.includes(d.gam_network_id)
            );
            selectedFilterType = -1;
          }
        }
        filterTabs.push(customNetworkGroupsTab);

        const { subFilterTabs, trialFilterTabs, productFilterTabs } =
          this._parsedSubFilter(groupedNetwork);

        const filteredData = orderNetworksBy(
          groupedNetwork,
          this.state.selectedSortType,
          this.state.selectedSortByDayType,
          this.state.isOrderByDesc
        );

        const networkInfo = _.map(snapshot.data, (d) => ({
          networkId: d.gam_network_id,
          name: d.name,
          status: d.status,
          product: d.product,
          trialStatus: d.trial_status.trialType,
        }));

        this.setState({
          snapshotKey: snapshot.snapshotKey,
          data: snapshot.data,
          dataGroupedByNetworkStatus,
          reportCreateTime: snapshot.reportCreateTime,

          selectedFilterType,
          filterTabs,
          subFilterTabs,
          trialFilterTabs,
          productFilterTabs,

          filteredData,
          paginatedData: this.repaginateData(filteredData),
          ...customState,
          networkInfo,
        });
      }
    } catch (err) {
      console.log("Error querying snapshot", err);
      this.setState({ errMsg: typeof err === "object" ? err.toString() : err });
    }

    this.setState({ isLoading: false });
  }

  handleFilterTypeChange(filterType) {
    this.setState({
      selectedFilterType: filterType,
      // reset trialType to ALL
      selectedSubFilter: subFilterTypes.ALL,
    });
    this.handleFilterNetworks(filterType);
  }

  handleSubFilterChange(subFilter) {
    this.setState({ selectedSubFilter: subFilter });
    this.handleFilterNetworks(this.state.selectedFilterType, subFilter);
  }

  toggleSetTwd() {
    this.setState({ isInTwd: !this.state.isInTwd });
  }

  handleSearch(inputValue) {
    // 1. show new search tab
    // 2. filter data
    const data = this.state.data;
    let filteredData = [];
    if (inputValue === "") {
      // Remain empty if no inputValue
      // filteredData = orderNetworksBy(
      //   data,
      //   this.state.selectedSortType,
      //   this.state.selectedSortByDayType,
      //   this.state.isOrderByDesc
      // );
    } else {
      const searchedData = searchNetworkByInput(data, inputValue, {
        isYieldSetReport: false,
      });
      filteredData = orderNetworksBy(
        searchedData,
        this.state.selectedSortType,
        this.state.selectedSortByDayType,
        this.state.isOrderByDesc
      );
    }
    this.setState(
      {
        selectedFilterType: null,
        isInSearchingMode: inputValue !== "",
        filteredData,
        numberOfSearchedData: filteredData.length,
        paginatedData: this.repaginateData(filteredData),
      },
      () => {
        const filterValue = _.get(
          this.state.filterTabs,
          [0, "filterValue"],
          null
        );
        if (inputValue === "" && !_.isNil(filterValue)) {
          this.handleFilterTypeChange(filterValue);
        }
      }
    );
  }

  handleFilterNetworks(filterType, subFilter) {
    let data = [];

    const stateData = {};
    if (subFilter && subFilter !== subFilterTypes.ALL) {
      const dataOfStatus = this.getDataByFilterType(filterType);

      data = _.filter(dataOfStatus, (d) => {
        if (_.includes(trialTypes, subFilter))
          return d.trial_status.trialType === subFilter;
        if (networkProduct[subFilter]) return d.product === subFilter;
        return false;
      });
    } else {
      data = this.getDataByFilterType(filterType);

      // [status.RUNNING, favorites (-1)]
      // Re-calculate sub / trial / product filters
      if ([networkStatus.RUNNING, -1].includes(filterType)) {
        const { subFilterTabs, trialFilterTabs, productFilterTabs } =
          this._parsedSubFilter(data);
        stateData.subFilterTabs = subFilterTabs;
        stateData.trialFilterTabs = trialFilterTabs;
        stateData.productFilterTabs = productFilterTabs;
      }
    }

    const filteredData = orderNetworksBy(
      data,
      this.state.selectedSortType,
      this.state.selectedSortByDayType,
      this.state.isOrderByDesc
    );

    this.setState({
      isInSearchingMode: false,
      filteredData,
      paginatedData: this.repaginateData(filteredData),
      ...stateData,
    });
  }

  handleSortTypeChange(newSortType) {
    if (newSortType === this.state.selectedSortType) {
      return;
    }

    const filteredData = orderNetworksBy(
      this.state.filteredData,
      newSortType,
      this.state.selectedSortByDayType,
      this.state.isOrderByDesc
    );

    this.setState({
      selectedSortType: newSortType,
      filteredData,
      paginatedData: this.repaginateData(filteredData),
    });
  }

  handleSortByDayTypeChange(newSortByDayType) {
    if (newSortByDayType === this.state.selectedSortByDayType) {
      return;
    }

    const filteredData = orderNetworksBy(
      this.state.filteredData,
      this.state.selectedSortType,
      newSortByDayType,
      this.state.isOrderByDesc
    );

    this.setState({
      selectedSortByDayType: newSortByDayType,
      filteredData,
      paginatedData: this.repaginateData(filteredData),
    });
  }

  toggleOrderBy() {
    const filteredData = orderNetworksBy(
      this.state.filteredData,
      this.state.selectedSortType,
      this.state.selectedSortByDayType,
      !this.state.isOrderByDesc
    );

    this.setState({
      isOrderByDesc: !this.state.isOrderByDesc,
      filteredData,
      paginatedData: this.repaginateData(filteredData),
    });
  }

  handlePresetProcess(preset) {
    const { filteredData } = this.state;
    const config = SNAPSHOT_PRESET_CONFIGS[preset];

    if (!config) return;

    const { selectedSortByDayType, selectedSortType, isOrderByDesc } = config;

    if (
      selectedSortByDayType === this.state.selectedSortByDayType &&
      selectedSortType === this.state.selectedSortType &&
      isOrderByDesc === this.state.isOrderByDesc
    ) {
      return;
    }

    const stateData = {
      selectedSortByDayType,
      selectedSortType,
      isOrderByDesc,
    };

    const newFilteredData = orderNetworksBy(
      filteredData,
      stateData.selectedSortType,
      stateData.selectedSortByDayType,
      stateData.isOrderByDesc
    );

    this.setState({
      ...stateData,
      filteredData: newFilteredData,
      paginatedData: this.repaginateData(newFilteredData),
    });
  }

  repaginateData(filteredData) {
    const nth = NUM_PER_PAGE;
    const paginatedData = _.slice(filteredData, 0, nth);
    this.itemRefs = paginatedData.map(() => React.createRef());
    return paginatedData;
  }

  handleLoadMore() {
    const nth = this.state.paginatedData.length + NUM_PER_PAGE;
    const paginatedData = _.slice(this.state.filteredData, 0, nth);
    this.itemRefs = paginatedData.map(() => React.createRef());

    this.setState({
      paginatedData,
    });
  }

  async handleCreateNetworkSnapshot(networkId) {
    await SnapshotAPI.createNetworkSnapshot({
      networkIds: [networkId],
    });
  }

  async handleCreateYieldSetSnapshot(networkId) {
    await SnapshotAPI.createYieldSetSnapshot({ networkIds: [networkId] });
  }

  async handleForceRefreshSnapshot(network, isYieldSet) {
    const userConfirm = window.confirm(
      `Refresh ${isYieldSet ? "yieldset " : ""}data for network ${
        network.gam_network_id
      } ${network.name}? (This might take a few seconds)`
    );
    if (!userConfirm) return;

    const { paginatedData } = this.state;
    const n = _.find(paginatedData, { gam_network_id: network.gam_network_id });
    n.isLoading = true;
    this.setState({ paginatedData });

    try {
      if (isYieldSet) {
        await this.handleCreateYieldSetSnapshot(network.gam_network_id);
      } else {
        await this.handleCreateNetworkSnapshot(network.gam_network_id);
      }

      const userConfirm2 = window.confirm(
        `${isYieldSet ? "Yieldset " : ""}Data for network ${
          network.gam_network_id
        } ${network.name} refreshed. Reload page to get the latest data.`
      );
      if (!userConfirm2) return;

      window.location.reload(false);
    } catch (err) {
      notify.show(
        `Failed to refresh ${isYieldSet ? "yieldset " : ""}data for network ${
          network.gam_network_id
        } ${network.name}. ${err.toString()}`,
        "error"
      );
      console.log(err);
    }

    // setTimeout(() => {
    //   const userConfirm2 = window.confirm(
    //     `Data for network ${network.gam_network_id} ${network.name} refreshed. Reload page to get the latest data`
    //   );
    //   if (!userConfirm2) return;

    //   window.location.reload(false);
    // }, 3000);
  }

  handleCustomNetworkGroupingChanged(value) {
    const { filterTabs } = this.state;
    const newFilterTabs = [...filterTabs];
    const favIndex = filterTabs.length - 1;

    newFilterTabs[favIndex].title = `Favorites (${value.length})`;

    this.setState(
      {
        filterTabs: newFilterTabs,
        customNetworkGroups: value,
      },
      () => {
        if (this.state.selectedFilterType === -1) {
          this.handleFilterTypeChange(-1);
        }
      }
    );
  }

  getDataByFilterType(filterType) {
    if (filterType === -1) {
      return this.state.data.filter((d) =>
        this.state.customNetworkGroups.includes(d.gam_network_id)
      );
    } else {
      return _.get(this.state.dataGroupedByNetworkStatus, filterType, []);
    }
  }

  _parsedSubFilter(networks) {
    const networksGroupByTrialType = _.groupBy(
      networks,
      "trial_status.trialType"
    );
    const networksGroupByProduct = _.groupBy(networks, "product");
    const title = _.startCase(_.lowerCase(subFilterTypes.ALL));
    const subFilterTabs = [
      {
        title: `${title} (${networks && networks.length})`,
        filterValue: subFilterTypes.ALL,
      },
    ];

    const trialFilterTabs = _.map(trialTypes, (trialType) => {
      const title = _.startCase(_.lowerCase(trialType));

      const numOfNetworksByFilter = networksGroupByTrialType[trialType]
        ? networksGroupByTrialType[trialType].length
        : 0;

      return {
        title: `${title} (${numOfNetworksByFilter})`,
        filterValue: trialType,
      };
    });

    const productFilterTabs = _.map(networkProduct, (value, key) => {
      const numOfNetworksByFilter = networksGroupByProduct[_.parseInt(key)]
        ? networksGroupByProduct[_.parseInt(key)].length
        : 0;

      return {
        title: `${value} (${numOfNetworksByFilter})`,
        filterValue: _.parseInt(key),
      };
    });

    return { subFilterTabs, trialFilterTabs, productFilterTabs };
  }

  render() {
    const {
      data,
      filteredData,
      paginatedData,
      reportCreateTime,
      isInTwd,
      showTrendTable,
      isOrderByDesc,
      selectedSortType,
      selectedSortByDayType,
      selectedFilterType,
      selectedSubFilter,
      filterTabs,
      subFilterTabs,
      trialFilterTabs,
      productFilterTabs,
      isInSearchingMode,
      numberOfSearchedData,
      selectedTabKey,
      selectedIndKey,
      inputValue,
      customData,
      customTabs,
      customIndicators,
      customUnits,
      networkInfo,
    } = this.state;

    const infoList = paginatedData
      ? paginatedData.map((item) => ({
          mainId: item.gam_network_id,
          mainTitle: item.name,
          subId: item.publisher_id,
          subTitle: item.publisher_name,
          timezone: item.timezone,
          updatedAt: item.updated_at,
        }))
      : [];

    return (
      <>
        {this.state.isLoading && (
          <div className="text-white">
            Preparing the newest snapshot data...
          </div>
        )}
        <Suspense fallback={<LoadingUI></LoadingUI>}>
          {this.state.errMsg && (
            <div className="text-white">{this.state.errMsg}</div>
          )}
          {data && (
            <div style={{ paddingBottom: "10rem" }}>
              <FixedWidgetContainer>
                <ToolBoxWidget dataRef={this.widgetRef} />
                {infoList.length !== 0 && (
                  <NetworkStickyTimeWidget
                    ref={this.widgetRef}
                    itemRefs={this.itemRefs}
                    itemList={infoList}
                  ></NetworkStickyTimeWidget>
                )}
              </FixedWidgetContainer>

              <div className="sticky top-0 z-10">
                <div>
                  <QuickNavigateHeader
                    data={data}
                    isInTwd={isInTwd}
                  ></QuickNavigateHeader>
                </div>
                <div>
                  <SnapshotViewHeader
                    reportCreateTime={reportCreateTime}
                    toggleSetTwd={this.toggleSetTwd}
                    isInTwd={isInTwd}
                    showTrendTable={showTrendTable}
                    selectedSortType={selectedSortType}
                    selectedSortByDayType={selectedSortByDayType}
                    handleSortByDayTypeChange={this.handleSortByDayTypeChange}
                    handlePresetProcess={this.handlePresetProcess}
                    isOrderByDesc={isOrderByDesc}
                    isInSearchingMode={isInSearchingMode}
                    handleSearch={this.handleSearch}
                    numberOfSearchedData={numberOfSearchedData}
                    handleSortTypeChange={this.handleSortTypeChange}
                    toggleOrderBy={this.toggleOrderBy}
                    filterTabs={filterTabs}
                    subFilterTabs={subFilterTabs}
                    trialFilterTabs={trialFilterTabs}
                    productFilterTabs={productFilterTabs}
                    networkInfo={networkInfo}
                    selectedFilterType={selectedFilterType}
                    handleFilterTypeChange={this.handleFilterTypeChange}
                    selectedSubFilter={selectedSubFilter}
                    handleSubFilterChange={this.handleSubFilterChange}
                    handleCustomNetworkGroupingChanged={
                      this.handleCustomNetworkGroupingChanged
                    }
                  ></SnapshotViewHeader>
                </div>
              </div>

              {paginatedData &&
                paginatedData.map((network, index) => {
                  return (
                    <NetworkWrap
                      key={network.gam_network_id}
                      innerRef={this.itemRefs[index]}
                      network={network}
                      isInTwd={isInTwd}
                      showTrendTable={showTrendTable}
                      selectedSortType={selectedSortType}
                      selectedTabKey={selectedTabKey}
                      selectedIndKey={selectedIndKey}
                      inputValue={inputValue}
                      customData={customData}
                      customTabs={customTabs}
                      customUnits={customUnits}
                      customIndicators={customIndicators}
                      handleForceRefreshSnapshot={
                        this.handleForceRefreshSnapshot
                      }
                      handleNavigatorPanelChange={(key, value) =>
                        this.setState({ [key]: value })
                      }
                      handleUnitTabsChange={(value) =>
                        this.setState({ customTabs: value })
                      }
                      handleCustomUnitsChanged={(value) =>
                        this.setState({ customUnits: value })
                      }
                      handleIndicatorChange={(key, value) => {
                        this.setState({ [key]: value });
                      }}
                    ></NetworkWrap>
                  );
                })}

              {paginatedData.length === filteredData.length ? (
                ""
              ) : (
                <button
                  type="button"
                  className="w-full bg-indigo-500 p-4 font-bold text-white hover:bg-indigo-600"
                  onClick={this.handleLoadMore}
                >
                  Load more
                </button>
              )}
            </div>
          )}
        </Suspense>
      </>
    );
  }
}

export default SnapshotViewer;
